/* Scoped lifecycle filter and optional cart only. No auth, credits or entitlements. */
(() => {
  'use strict';
  const instances = new WeakMap();
  const zh = () => (document.documentElement.lang || '').toLowerCase().startsWith('zh');
  const message = (cn, en) => zh() ? cn : en;

  function mount(panel) {
    if (instances.has(panel)) return;
    const controller = new AbortController();
    instances.set(panel, controller);
    const phase = panel.querySelector('[data-toolbox-phase]');
    const cards = Array.from(panel.querySelectorAll('[data-toolbox-card]'));
    const status = panel.querySelector('[data-toolbox-status]');
    const addButtons = panel.querySelectorAll('[data-toolbox-add]');
    if (!phase || !status) return;
    const root = panel.closest('[data-gtender-public-entry]');
    let pending = false;
    const applyPhase = () => {
      const selection = phase.value;
      let count = 0;
      cards.forEach(card => {
        const visible = selection === 'all' || card.dataset.phase === selection;
        card.hidden = !visible;
        if (visible) count += 1;
      });
      status.textContent = message(`顯示 ${count} 項服務。階段由你選擇，並非自動資格判定。`, `${count} services shown. Stage selection is not an eligibility decision.`);
    };
    // Progressive enhancement: without JS all products remain accessible.
    phase.value = '1';
    applyPhase();
    phase.addEventListener('change', applyPhase, {signal: controller.signal});
    // Reset when a different tender is selected; never carry recommendations across tenders.
    const observer = root ? new MutationObserver(() => {
      phase.value = '1';
      applyPhase();
    }) : null;
    if (observer) observer.observe(root, {attributes: true, attributeFilter: ['data-gtender-active-detail-id']});
    controller.signal.addEventListener('abort', () => observer?.disconnect(), {once: true});
    addButtons.forEach(button => { button.hidden = false; });
    panel.addEventListener('click', async event => {
      const button = event.target instanceof Element ? event.target.closest('[data-toolbox-add]') : null;
      if (!button || !panel.contains(button) || pending) return;
      const variantId = button.dataset.variantId || '';
      if (!/^\d+$/.test(variantId)) return;
      const endpoint = new URL(panel.dataset.cartAddUrl, location.origin);
      if (endpoint.origin !== location.origin || !endpoint.pathname.endsWith('/cart/add.js')) return;
      pending = true;
      addButtons.forEach(node => { node.disabled = true; });
      panel.setAttribute('aria-busy', 'true');
      status.textContent = message('正在加入購物車…', 'Adding to cart…');
      const request = new AbortController();
      const timeout = setTimeout(() => request.abort(), 15000);
      const abortRequest = () => request.abort();
      controller.signal.addEventListener('abort', abortRequest, {once: true});
      try {
        const response = await fetch(endpoint.href, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
          body: JSON.stringify({items: [{id: variantId, quantity: 1}]}),
          signal: request.signal
        });
        const result = await response.json();
        if (!response.ok || !Array.isArray(result.items) || result.items.length === 0) throw new Error('cart_add_failed');
        if (panel.isConnected) status.textContent = message('已加入購物車，尚未付款或開通服務。', 'Added to cart. Payment and service activation are not complete.');
        // No automatic unlock, debit, retry, checkout redirect or theme-specific cart event.
      } catch (error) {
        if (panel.isConnected) status.textContent = message('未能確認加購結果。請先檢查購物車，避免重複加入。', 'Could not confirm the result. Check your cart before trying again.');
      } finally {
        clearTimeout(timeout);
        controller.signal.removeEventListener('abort', abortRequest);
        pending = false;
        panel.removeAttribute('aria-busy');
        addButtons.forEach(node => { node.disabled = false; });
      }
    }, {signal: controller.signal});
  }
  const init = (scope = document) => {
    if (scope.matches?.('[data-gtender-toolbox]')) mount(scope);
    scope.querySelectorAll('[data-gtender-toolbox]').forEach(mount);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once: true});
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-gtender-toolbox]').forEach(panel => {
      instances.get(panel)?.abort();
      instances.delete(panel);
    });
  });
})();
